import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import {
  MESSAGE_TYPE_OPTIONS,
  SP_MESSAGE_SUBJECT_OPTIONS,
} from '../constants/messageTypeOptions';
import { writePendingIntent } from '../utils/pendingIntentStorage';
import type { FpSpMessageType, SpMessageSubject } from '../types';

const RADIO_GROUP_LABEL = 'Choose your conversation goal';

/** 'fa' = FA→SP first-contact catalog (086, default); 'sp' = SP subject catalog (102). */
export type PreChatIntentCatalog = 'fa' | 'sp';

type CatalogValue = FpSpMessageType | SpMessageSubject;

type PreChatIntentModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selects the option list and which pending-intent field Continue writes. */
  catalog?: PreChatIntentCatalog;
  /** Display name of the recipient being contacted, shown in the context panel. */
  providerName: string;
  activeWorkspaceOwnerId: string;
  /** Required for SP-targeted launches ('fa' catalog); absent when an SP contacts an FA. */
  serviceProviderId?: string;
  recipientUserId: string;
  /** Only ever set when the launch originated from that SP's full-profile view. */
  profileViewEngagementId?: string;
  sourceSurface: string;
  /** Called after the pending intent is written — the caller navigates (ticket 08 wires this). */
  onContinue: () => void;
};

/**
 * FA/subdelegate → SP first-contact intent picker (spec §4, approved
 * prototype variant "C — Guided handoff"). Also serves the empty-thread
 * deep-link fallback composition (§3.5) — same component, no variant.
 *
 * Only the Continue action writes anything: cancel, the dialog's close
 * button, Escape, and an outside click all resolve to the same
 * `onOpenChange(false)` call Radix already wires up, so none of them touch
 * pending-intent storage.
 */
export const PreChatIntentModal = ({
  open,
  onOpenChange,
  catalog = 'fa',
  providerName,
  activeWorkspaceOwnerId,
  serviceProviderId,
  recipientUserId,
  profileViewEngagementId,
  sourceSurface,
  onContinue,
}: PreChatIntentModalProps) => {
  const [selected, setSelected] = useState<CatalogValue | null>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);

  const options: ReadonlyArray<{ value: CatalogValue; label: string }> =
    catalog === 'sp' ? SP_MESSAGE_SUBJECT_OPTIONS : MESSAGE_TYPE_OPTIONS;

  // No default selection every time the modal (re)opens — a previous
  // cancelled attempt must never leave a stale choice behind.
  useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const handleContinue = () => {
    if (!selected) return;
    writePendingIntent({
      activeWorkspaceOwnerId,
      recipientUserId,
      ...(serviceProviderId ? { serviceProviderId } : {}),
      // `selected` always came from the active catalog's own option list, so
      // the narrowing casts are safe.
      ...(catalog === 'sp'
        ? { spMessageSubject: selected as SpMessageSubject }
        : { messageType: selected as FpSpMessageType }),
      ...(profileViewEngagementId ? { profileViewEngagementId } : {}),
      sourceSurface,
    });
    onContinue();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          firstRadioRef.current?.focus();
        }}
        className="grid max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-[960px] grid-cols-1 gap-0 overflow-x-hidden overflow-y-auto p-0 sm:grid-cols-[280px_minmax(0,1fr)]"
      >
        <aside className="bg-[#026857] p-6 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/65">
            New conversation
          </p>
          <h2 className="mt-2 text-xl font-semibold leading-tight">{providerName}</h2>
          <p className="mt-4 text-sm leading-6 text-white/75">
            What brings you here today? Choose what best fits your conversation.
          </p>
        </aside>

        <section className="min-w-0 p-6">
          <DialogHeader>
            {/* Radix owns DialogTitle's id for its own aria-labelledby wiring on
                the dialog role — don't override it. The radio group below
                re-states the exact same text as its own accessible name. */}
            <DialogTitle>{RADIO_GROUP_LABEL}</DialogTitle>
            <DialogDescription>Choose a reason, then continue to your message.</DialogDescription>
          </DialogHeader>

          <fieldset
            role="group"
            aria-label={RADIO_GROUP_LABEL}
            className="mt-5 divide-y rounded-xl border"
          >
            {options.map((option, index) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-3 p-3.5 hover:bg-muted/50"
              >
                <span
                  aria-hidden="true"
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    selected === option.value ? 'bg-[#026857] text-white' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium">{option.label}</span>
                <input
                  ref={index === 0 ? firstRadioRef : undefined}
                  type="radio"
                  name="preChatIntent"
                  value={option.value}
                  checked={selected === option.value}
                  onChange={() => setSelected(option.value)}
                  aria-label={option.label}
                  className="h-4 w-4 accent-[#026857]"
                />
              </label>
            ))}
          </fieldset>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={!selected}
              onClick={handleContinue}
              className="bg-[#026857] hover:bg-[#025247]"
            >
              Continue
            </Button>
          </DialogFooter>
        </section>
      </DialogContent>
    </Dialog>
  );
};
